import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [idleNote] = useState(() => {
    try {
      if (sessionStorage.getItem("equipr:idle-logout")) { sessionStorage.removeItem("equipr:idle-logout"); return true; }
    } catch { /* ignore */ }
    return false;
  });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate(res.role === "super_admin" ? "/tenants" : "/");
    else setError(res.error);
  };

  return (
    <div className="h-screen w-full flex bg-equipr-black">
      {/* Left brand panel */}
      <div className="hidden md:flex flex-col justify-between w-1/2 p-12 bg-equipr-black relative overflow-hidden">
        <div className="absolute -right-20 -top-20 w-96 h-96 bg-equipr-yellow/10 rounded-full blur-3xl" />
        <BrandLogo variant="dark" />
        <div className="relative z-10">
          <h1 className="text-white text-4xl font-extrabold leading-tight">
            Plan Better.<br />
            <span className="text-equipr-yellow">Work Better.</span>
          </h1>
          <p className="text-gray-400 mt-4 max-w-sm text-sm">
            Manage the hire of your assets and operators to every project — schedule, off-hire and
            stand down, all in one place.
          </p>
        </div>
        <p className="text-gray-600 text-xs relative z-10">© EQUIPR Software</p>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center bg-white p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="md:hidden mb-8">
            <BrandLogo />
          </div>
          <h2 className="text-2xl font-bold text-equipr-black">Sign in</h2>
          <p className="text-sm text-gray-500 mb-6">Access your allocation workspace</p>

          {idleNote && (
            <div className="mb-4 border border-amber-300 bg-amber-50 text-amber-800 text-sm px-3 py-2 rounded-[8px]" data-testid="idle-logout-note">
              You were signed out due to inactivity. Please sign in again.
            </div>
          )}

          {error && (
            <div className="mb-4 border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2" data-testid="login-error">
              {error}
            </div>
          )}

          <label className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-ink-muted mb-1.5 block">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="login-email-input"
            className="w-full h-9 px-3 border border-border-control bg-white text-[13.5px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none focus:border-equipr-black mb-4"
            placeholder="you@company.com"
          />

          <label className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-ink-muted mb-1.5 block">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            data-testid="login-password-input"
            className="w-full h-9 px-3 border border-border-control bg-white text-[13.5px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none focus:border-equipr-black mb-6"
            placeholder="••••••••"
          />

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full h-10 bg-equipr-yellow text-equipr-black font-bold text-sm rounded-none hover:bg-equipr-yellow-dark transition-colors disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>

          <div className="mt-6 border border-gray-200 bg-gray-50 px-3 py-2.5" data-testid="demo-credentials">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1">Demo Login</p>
            <div className="flex items-center justify-between text-xs text-gray-700">
              <span>demo@equipr.software · Demo@2026</span>
              <button
                type="button"
                data-testid="demo-fill-button"
                onClick={() => { setEmail("demo@equipr.software"); setPassword("Demo@2026"); }}
                className="text-equipr-black font-semibold underline hover:text-accent-ink"
              >
                Use demo
              </button>
            </div>
          </div>

          <div className="mt-3 border border-gray-200 bg-gray-50 px-3 py-2.5" data-testid="admin-credentials">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1">Admin Login</p>
            <div className="flex items-center justify-between text-xs text-gray-700">
              <span>admin@equipr.software · Equipr@2026</span>
              <button
                type="button"
                data-testid="admin-fill-button"
                onClick={() => { setEmail("admin@equipr.software"); setPassword("Equipr@2026"); }}
                className="text-equipr-black font-semibold underline hover:text-accent-ink"
              >
                Use admin
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
